// Client mirror of the worker's reel time math + N1 normalize migration
// (apps/socialkit/src/core/visual/v2/video-time.ts). Keep the two in exact lockstep — the
// preview playhead, the timeline ruler, and the burned master all measure the same EFFECTIVE
// (crossfade-overlap-subtracted) seconds, so what sits at 0:07 on the timeline lands at 0:07
// in the mp4. See the worker file for the full contract.

import type {
  Layer,
  PostComposition,
  VideoBeatComposition,
  VideoComposition,
  VideoLayerClip,
} from './types'

/** Mirrors the container's concatFinal (render-server.mjs) EXACTLY: cut-joined beats concat
 *  losslessly (a cut is a TRUE cut, zero overlap), and ONLY 'crossfade' boundaries dissolve,
 *  each overlapping the join by CROSSFADE_SEC (clamped like the container's fade guard). */
export const CROSSFADE_SEC = 0.4
/** Minimum clip window the model keeps (shorter collapses to this). */
export const MIN_CLIP_SEC = 0.2

export type BeatSpan = { start: number; end: number }

/** Every beat's [start, end) on the EFFECTIVE (master) timeline. Spans overlap by the
 *  crossfade where a beat enters with one; cut boundaries are adjacent. */
export function videoBeatSpans(beats: VideoBeatComposition[]): BeatSpan[] {
  const spans: BeatSpan[] = []
  let acc = 0
  for (let i = 0; i < beats.length; i++) {
    const dur = beats[i]!.durationSec
    let start = acc
    if (i > 0 && beats[i]!.transitionIn === 'crossfade') {
      start = acc - Math.max(0.02, Math.min(CROSSFADE_SEC, dur - 0.1, acc - 0.1))
    }
    spans.push({ start, end: start + dur })
    acc = start + dur
  }
  return spans
}

/** The master's total duration in effective seconds (= last span end). */
export function videoTotalDuration(beats: VideoBeatComposition[]): number {
  return videoBeatSpans(beats).at(-1)?.end ?? 0
}

/** The beat index whose span CONTAINS t (during a crossfade overlap: the INCOMING beat). */
export function beatIndexAt(beats: VideoBeatComposition[], t: number): number {
  const spans = videoBeatSpans(beats)
  for (let i = spans.length - 1; i >= 0; i--) {
    if (t >= spans[i]!.start - 1e-6) return i
  }
  return 0
}

/** The clips whose window intersects [from, to), z-ascending. Hidden layers excluded. */
export function clipsInWindow(clips: VideoLayerClip[], from: number, to: number): VideoLayerClip[] {
  return clips
    .filter(c => !c.layer.hidden && c.start < to - 1e-6 && c.end > from + 1e-6)
    .sort((a, b) => a.layer.z - b.layer.z)
}

/** The ACTIVE-BEAT EDITOR VIEW: a synthesized PostComposition holding every clip layer whose
 *  window intersects the beat's span (hidden ones included, so the layers panel can unhide),
 *  z-ascending, over the beat overlay's canvas/brand style template. The provider reconciles
 *  editor mutations back into the deck clips by layer id. */
export function videoEditorViewDoc(v: VideoComposition, beatIndex: number): PostComposition {
  const span = videoBeatSpans(v.beats)[beatIndex] ?? { start: 0, end: 0 }
  const beat = v.beats[Math.max(0, Math.min(beatIndex, v.beats.length - 1))]!
  const layers = (v.layers ?? [])
    .filter(c => c.start < span.end - 1e-6 && c.end > span.start + 1e-6)
    .sort((a, b) => a.layer.z - b.layer.z)
    .map(c => c.layer)
  return { ...beat.overlay, layers, meta: { ...beat.overlay.meta, dirty: false } }
}

/** The PLAYBACK VIEW for a beat: like the editor view, but each layer carries a synthesized
 *  beat-relative `timing` (its clip window shifted by the beat's span start, UN-clamped) so
 *  CompositionCanvas's beatTime gating + anim styles preview windows/entrances exactly. */
export function videoBeatPlaybackDoc(v: VideoComposition, beatIndex: number): PostComposition {
  const span = videoBeatSpans(v.beats)[beatIndex] ?? { start: 0, end: 0 }
  const beat = v.beats[Math.max(0, Math.min(beatIndex, v.beats.length - 1))]!
  const layers = clipsInWindow(v.layers ?? [], span.start, span.end).map(c => ({
    ...c.layer,
    timing: { start: c.start - span.start, end: c.end - span.start },
  }))
  return { ...beat.overlay, layers, meta: { ...beat.overlay.meta, dirty: false } }
}

/** Map a pre-N1 NOMINAL absolute time (Σ durations, no overlap) onto the effective timeline. */
function nominalToEffective(beats: VideoBeatComposition[], t: number): number {
  const spans = videoBeatSpans(beats)
  let acc = 0
  for (let i = 0; i < beats.length; i++) {
    const dur = beats[i]!.durationSec
    if (t <= acc + dur + 1e-6 || i === beats.length - 1) {
      return spans[i]!.start + Math.max(0, Math.min(t - acc, dur))
    }
    acc += dur
  }
  return t
}

/** True once a document carries the N1 deck-level clip representation. */
export function isNormalizedVideo(v: VideoComposition): boolean {
  return Array.isArray(v.layers)
}

/** Lift a pre-N1 document into the deck-level clip representation (idempotent). Mirror of
 *  the worker's normalizeVideoComposition — see that file for the rules. */
export function normalizeVideoComposition(v: VideoComposition): VideoComposition {
  // Already normalized AND no stray content outside the clip deck → identity. A layer some
  // stale writer put back into a beat overlay (or a re-introduced `pinned`) is LIFTED below
  // instead of silently never rendering.
  if (isNormalizedVideo(v) && !v.pinned && v.beats.every(b => b.overlay.layers.length === 0))
    return v
  const spans = videoBeatSpans(v.beats)
  const total = spans.at(-1)?.end ?? 0
  const clips: VideoLayerClip[] = [...(v.layers ?? [])]
  let maxZ = clips.reduce((m, c) => Math.max(m, c.layer.z), 0)
  // Clamp a lifted window inside its beat span (the MIN_CLIP floor must never spill past the
  // beat into the next one, or past the reel end where anims silently die). Returns null for
  // a window that never truly starts inside its beat (legacy garbage like timing past the
  // beat end) — resurrecting it via clamping would show text the user never saw.
  const fit = (
    start: number,
    rawEnd: number,
    spanEnd: number
  ): { start: number; end: number } | null => {
    const cap = Math.min(spanEnd, total)
    if (start >= cap - 1e-6) return null
    let end = Math.max(rawEnd, start + MIN_CLIP_SEC)
    if (end > cap) {
      end = cap
      start = Math.min(start, Math.max(0, end - MIN_CLIP_SEC))
    }
    return { start, end }
  }
  v.beats.forEach((b, i) => {
    const span = spans[i]!
    for (const l of b.overlay.layers) {
      maxZ = Math.max(maxZ, l.z)
      const start = span.start + Math.max(0, l.timing?.start ?? 0)
      const rawEnd = l.timing ? span.start + Math.min(l.timing.end, b.durationSec) : span.end
      const win = fit(start, rawEnd, span.end)
      if (!win) continue
      const { timing: _t, ...layer } = l
      clips.push({ layer: layer as Layer, ...win })
    }
  })
  const pins = [...(v.pinned ?? [])].sort((a, b) => a.layer.z - b.layer.z)
  pins.forEach((p, i) => {
    const { timing: _t, ...layer } = p.layer
    const start = nominalToEffective(v.beats, p.start)
    const rawEnd = Math.max(nominalToEffective(v.beats, p.end), start + MIN_CLIP_SEC)
    const win = fit(start, rawEnd, total)
    if (!win) return
    clips.push({ layer: { ...(layer as Layer), z: maxZ + 1 + i }, ...win })
  })
  const beats = v.beats.map(b => ({ ...b, overlay: { ...b.overlay, layers: [] } }))
  const { pinned: _p, ...rest } = v
  return { ...rest, beats, layers: clips }
}
